import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import moment from 'moment'
import { viewSubscriptionPlanDetails } from '../../slice/subscriptionPlanSlice'

const ViewSubscriptionPlanDetail = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const location = useLocation()

    // Get planId from state
    const planId = location.state?.planId

    const { subscriptionPlanDetail } = useSelector(state => state.subscriptionPlan)

    useEffect(() => {
        if (!planId) {
            navigate('/subscriptions')
            return
        }
        dispatch(viewSubscriptionPlanDetails({ id: planId }))
    }, [planId, dispatch, navigate])

    if (!subscriptionPlanDetail) {
        return (
            <div className="text-center py-5">
                <i className="fe-alert-circle" style={{ fontSize: '48px', color: '#dc3545' }}></i>
                <h5 className="mt-3">No data found</h5>
                <button
                    className="btn btn-primary mt-3"
                    onClick={() => navigate('/subscriptions')}
                >
                    Go Back to Subscription Plans
                </button>
            </div>
        )
    }

    const {
        name,
        description,
        duration_type,
        duration_days,
        price,
        apple_product_id,
        google_product_id,
        is_active,
        is_deleted,
        createdAt,
        updatedAt,
        features = {}
    } = subscriptionPlanDetail

    const features_en = features?.en || []
    const features_ar = features?.ar || []

    // Get duration badge
    const getDurationBadge = () => {
        const durationConfig = {
            monthly: { bg: '#17a2b8', text: 'Monthly', icon: 'calendar' },
            quarterly: { bg: '#ffc107', text: 'Quarterly', icon: 'repeat' },
            yearly: { bg: '#28a745', text: 'Yearly', icon: 'star' },
            lifetime: { bg: '#6f42c1', text: 'Lifetime', icon: 'award' }
        }

        const config = durationConfig[duration_type] || { bg: '#6c757d', text: duration_type, icon: 'clock' }

        return (
            <span
                style={{
                    backgroundColor: config.bg,
                    color: 'white',
                    padding: '8px 16px',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: '600',
                    textTransform: 'capitalize',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px'
                }}
            >
                <i className={`fe-${config.icon}`} style={{ fontSize: '14px' }}></i>
                {config.text}
            </span>
        )
    }

    // Check if plan is free
    const isFree = () => {
        return price === 0 || price === '0'
    }

    return (
        <div className="container-fluid px-4 py-3">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="fw-semibold mb-0">Subscription Plan Details</h4>
                <button
                    className="btn btn-outline-secondary"
                    onClick={() => navigate('/subscriptions')}
                    style={{
                        padding: '8px 20px',
                        fontSize: '14px',
                        borderRadius: '6px'
                    }}
                >
                    ← Back to List
                </button>
            </div>

            {/* Plan Preview Card */}
            <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '12px', overflow: 'hidden' }}>
                <div
                    className="card-body p-5"
                    style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white'
                    }}
                >
                    <div className="text-center">
                        {/* Status badges */}
                        <div className="mb-3">
                            {is_active === 1 ? (
                                <span className="badge bg-success" style={{ fontSize: '12px', padding: '6px 12px' }}>
                                    <i className="fe-check-circle" style={{ fontSize: '12px' }}></i> Active
                                </span>
                            ) : (
                                <span className="badge bg-secondary" style={{ fontSize: '12px', padding: '6px 12px' }}>
                                    <i className="fe-minus-circle" style={{ fontSize: '12px' }}></i> Inactive
                                </span>
                            )}
                            {is_deleted === 1 && (
                                <span className="badge bg-danger ms-2" style={{ fontSize: '12px', padding: '6px 12px' }}>
                                    <i className="fe-trash-2" style={{ fontSize: '12px' }}></i> Deleted
                                </span>
                            )}
                        </div>

                        {/* Plan Name */}
                        <h2
                            style={{
                                fontSize: '42px',
                                fontWeight: '700',
                                marginBottom: '20px',
                                textShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}
                        >
                            {name}
                        </h2>

                        {/* Price */}
                        <div className="mb-4">
                            {isFree() ? (
                                <div
                                    style={{
                                        fontSize: '48px',
                                        fontWeight: '700',
                                        textShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                    }}
                                >
                                    FREE
                                </div>
                            ) : (
                                <div>
                                    <span
                                        style={{
                                            fontSize: '24px',
                                            fontWeight: '400',
                                            opacity: 0.9
                                        }}
                                    >
                                        AED
                                    </span>
                                    <span
                                        style={{
                                            fontSize: '56px',
                                            fontWeight: '700',
                                            margin: '0 8px',
                                            textShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                        }}
                                    >
                                        {parseFloat(price).toFixed(0)}
                                    </span>
                                    <span
                                        style={{
                                            fontSize: '24px',
                                            fontWeight: '400',
                                            opacity: 0.9
                                        }}
                                    >
                                        / {duration_type}
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* Description */}
                        {description && (
                            <p
                                style={{
                                    fontSize: '18px',
                                    maxWidth: '600px',
                                    margin: '0 auto 25px',
                                    opacity: 0.95,
                                    lineHeight: '1.6'
                                }}
                            >
                                {description}
                            </p>
                        )}

                        {/* Duration Badge */}
                        <div className="mb-3">
                            {getDurationBadge()}
                        </div>

                        {/* Duration Days */}
                        <div
                            style={{
                                fontSize: '14px',
                                opacity: 0.9
                            }}
                        >
                            <i className="fe-clock" style={{ fontSize: '14px' }}></i> {duration_days} days duration
                        </div>
                    </div>
                </div>
            </div>

            {/* Details Section */}
            <div className="row">
                {/* Plan Information */}
                <div className="col-md-6 mb-4">
                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '8px' }}>
                        <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0' }}>
                            <i className="fe-info me-2"></i>
                            Plan Information
                        </div>
                        <div className="card-body">
                            <div className="mb-3">
                                <small className="text-muted d-block mb-1">Plan Name</small>
                                <strong style={{ fontSize: '16px' }}>{name}</strong>
                            </div>

                            <div className="mb-3">
                                <small className="text-muted d-block mb-1">Description</small>
                                <strong>{description || 'NA'}</strong>
                            </div>

                            <div className="mb-3">
                                <small className="text-muted d-block mb-1">Duration Type</small>
                                <div className="mt-1">
                                    {getDurationBadge()}
                                </div>
                            </div>

                            <div>
                                <small className="text-muted d-block mb-1">Duration (Days)</small>
                                <strong style={{ fontSize: '20px', color: '#667eea' }}>
                                    {duration_days} days
                                </strong>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Pricing Information */}
                <div className="col-md-6 mb-4">
                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '8px' }}>
                        <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0' }}>
                            <i className="fe-dollar-sign me-2"></i>
                            Pricing Information
                        </div>
                        <div className="card-body">
                            <div className="mb-3">
                                <small className="text-muted d-block mb-1">Price</small>
                                <div>
                                    {isFree() ? (
                                        <span
                                            style={{
                                                fontWeight: '700',
                                                fontSize: '32px',
                                                color: '#28a745',
                                                backgroundColor: '#d4edda',
                                                padding: '12px 20px',
                                                borderRadius: '8px',
                                                border: '2px solid #c3e6cb',
                                                display: 'inline-block'
                                            }}
                                        >
                                            FREE
                                        </span>
                                    ) : (
                                        <div>
                                            <strong
                                                style={{
                                                    fontSize: '36px',
                                                    color: '#495057'
                                                }}
                                            >
                                                AED {parseFloat(price).toFixed(2)}
                                            </strong>
                                            <div className="mt-2">
                                                <span
                                                    className="badge bg-primary"
                                                    style={{ fontSize: '13px', padding: '6px 12px' }}
                                                >
                                                    {parseFloat(price / duration_days).toFixed(2)} AED per day
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div>
                                <small className="text-muted d-block mb-1">Billing Cycle</small>
                                <strong style={{ textTransform: 'capitalize' }}>
                                    {duration_type}
                                </strong>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Apple Configuration */}
                <div className="col-md-6 mb-4">
                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '8px' }}>
                        <div className="card-header bg-light fw-semibold border-0 d-flex align-items-center" style={{ borderRadius: '8px 8px 0 0' }}>
                            <i className="fe-smartphone me-2"></i>
                            Apple Configuration
                        </div>
                        <div className="card-body">
                            <div className="mb-3">
                                <small className="text-muted d-block mb-1">Product ID</small>
                                <div
                                    style={{
                                        fontFamily: 'monospace',
                                        fontSize: '13px',
                                        fontWeight: '600',
                                        color: '#495057',
                                        backgroundColor: '#f8f9fa',
                                        padding: '10px 12px',
                                        borderRadius: '6px',
                                        border: '1px solid #dee2e6',
                                        wordBreak: 'break-all'
                                    }}
                                >
                                    {apple_product_id || 'Not configured'}
                                </div>
                            </div>

                            <div className="alert alert-info mb-0" style={{ borderRadius: '6px' }}>
                                <i className="fe-info" style={{ fontSize: '14px' }}></i>
                                <small className="ms-2">
                                    This ID is used for Apple In-App Purchase integration
                                </small>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Google Configuration */}
                <div className="col-md-6 mb-4">
                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '8px' }}>
                        <div className="card-header bg-light fw-semibold border-0 d-flex align-items-center" style={{ borderRadius: '8px 8px 0 0' }}>
                            <i className="fe-smartphone me-2"></i>
                            Google Configuration
                        </div>
                        <div className="card-body">
                            <div className="mb-3">
                                <small className="text-muted d-block mb-1">Product ID</small>
                                <div
                                    style={{
                                        fontFamily: 'monospace',
                                        fontSize: '13px',
                                        fontWeight: '600',
                                        color: '#495057',
                                        backgroundColor: '#f8f9fa',
                                        padding: '10px 12px',
                                        borderRadius: '6px',
                                        border: '1px solid #dee2e6',
                                        wordBreak: 'break-all'
                                    }}
                                >
                                    {google_product_id || 'Not configured'}
                                </div>
                            </div>

                            <div className="alert alert-info mb-0" style={{ borderRadius: '6px' }}>
                                <i className="fe-info" style={{ fontSize: '14px' }}></i>
                                <small className="ms-2">
                                    This ID is used for Google Play In-App Purchase integration
                                </small>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Features */}
                <div className="col-12 mb-4">
                    <div className="card border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                        <div className="card-header bg-light fw-semibold border-0">
                            <i className="fe-list me-2"></i>
                            Plan Features
                        </div>

                        <div className="card-body">
                            <div className="row">

                                {/* English Features */}
                                <div className="col-md-6 mb-3">
                                    <small className="text-muted d-block mb-2">
                                        English Features
                                    </small>

                                    {features_en.length ? (
                                        <ul style={{ listStyle: "none", padding: 0 }}>
                                            {features_en.map((feature, index) => (
                                                <li
                                                    key={index}
                                                    style={{
                                                        marginBottom: "10px",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "8px"
                                                    }}
                                                >
                                                    <i className="fe-check-circle text-success"></i>
                                                    {feature}
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <span className="text-muted">
                                            No features available
                                        </span>
                                    )}
                                </div>

                                {/* Arabic Features */}
                                <div className="col-md-6 mb-3">
                                    <small className="text-muted d-block mb-2">
                                        Arabic Features
                                    </small>

                                    {features_ar.length ? (
                                        <ul
                                            style={{
                                                listStyle: "none",
                                                padding: 0,
                                                direction: "rtl",
                                                textAlign: "right"
                                            }}
                                        >
                                            {features_ar.map((feature, index) => (
                                                <li
                                                    key={index}
                                                    style={{
                                                        marginBottom: "10px",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "flex-end",
                                                        gap: "8px"
                                                    }}
                                                >
                                                    {feature}
                                                    <i className="fe-check-circle text-success"></i>
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <span className="text-muted">
                                            لا توجد ميزات
                                        </span>
                                    )}
                                </div>

                            </div>
                        </div>
                    </div>
                </div>

                {/* Status Information */}
                <div className="col-md-6 mb-4">
                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '8px' }}>
                        <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0' }}>
                            <i className="fe-toggle-right me-2"></i>
                            Status Information
                        </div>
                        <div className="card-body">
                            <div className="mb-3">
                                <small className="text-muted d-block mb-1">Active Status</small>
                                <div className="mt-1">
                                    {is_active === 1 ? (
                                        <span className="badge bg-success" style={{ padding: '8px 14px', fontSize: '13px' }}>
                                            <i className="fe-check-circle" style={{ fontSize: '12px' }}></i> Active
                                        </span>
                                    ) : (
                                        <span className="badge bg-secondary" style={{ padding: '8px 14px', fontSize: '13px' }}>
                                            <i className="fe-minus-circle" style={{ fontSize: '12px' }}></i> Inactive
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className="mb-3">
                                <small className="text-muted d-block mb-1">Deletion Status</small>
                                <div className="mt-1">
                                    {is_deleted === 1 ? (
                                        <span className="badge bg-danger" style={{ padding: '8px 14px', fontSize: '13px' }}>
                                            <i className="fe-trash-2" style={{ fontSize: '12px' }}></i> Deleted
                                        </span>
                                    ) : (
                                        <span className="badge bg-success" style={{ padding: '8px 14px', fontSize: '13px' }}>
                                            <i className="fe-check" style={{ fontSize: '12px' }}></i> Not Deleted
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div>
                                <small className="text-muted d-block mb-1">Availability</small>
                                <div className="mt-1">
                                    {is_active === 1 && is_deleted === 0 ? (
                                        <span className="badge bg-success" style={{ padding: '8px 14px', fontSize: '13px' }}>
                                            <i className="fe-check-circle" style={{ fontSize: '12px' }}></i> Available for Purchase
                                        </span>
                                    ) : (
                                        <span className="badge bg-warning text-dark" style={{ padding: '8px 14px', fontSize: '13px' }}>
                                            <i className="fe-alert-circle" style={{ fontSize: '12px' }}></i> Not Available
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Record Information */}
                <div className="col-md-6 mb-4">
                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '8px' }}>
                        <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0' }}>
                            <i className="fe-file-text me-2"></i>
                            Record Information
                        </div>
                        <div className="card-body">
                            <div className="mb-3">
                                <small className="text-muted d-block mb-1">Created At</small>
                                <strong>
                                    {createdAt
                                        ? moment(createdAt).local().format('DD MMM YYYY, hh:mm A')
                                        : 'NA'}
                                </strong>
                            </div>

                            <div className="mb-3">
                                <small className="text-muted d-block mb-1">Last Updated</small>
                                <strong>
                                    {updatedAt
                                        ? moment(updatedAt).local().format('DD MMM YYYY, hh:mm A')
                                        : 'NA'}
                                </strong>
                            </div>

                            <div>
                                <small className="text-muted d-block mb-1">Last Modified</small>
                                <strong>
                                    {updatedAt
                                        ? moment(updatedAt).fromNow()
                                        : 'NA'}
                                </strong>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Statistics (Placeholder) */}
                <div className="col-12 mb-4">
                    <div className="card border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                        <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0' }}>
                            <i className="fe-bar-chart-2 me-2"></i>
                            Subscription Statistics
                        </div>
                        <div className="card-body">
                            <div className="alert alert-info mb-0" style={{ borderRadius: '6px' }}>
                                <i className="fe-info me-2"></i>
                                Active subscriptions, revenue, and usage statistics will be displayed here when available.
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ViewSubscriptionPlanDetail